import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { canUserUpdateCompanySettings, isPremiumTierUser } from '@shared/utils/AccessControl';
import { Permission } from '@shared/models/user.models';
import { AccountType } from '@shared/models/company.models';
import { getParameterByName, setQueryParams } from '@shared/utils/UrlUtils';
import AccessRequired from '../components/AccessRequired';
import { SettingsPageLayout, SettingsRibbon, SettingsSidebarLayout } from '../components/SettingsSidebarLayout';
import { reset } from '../edit-company-page/editCompany.duck';
import { CompanyInformationView } from './data/CompanyManagement.model';
import { resetEditCompanyLayout, selectActivePanel, setActivePanel, } from './data/editCompanyLayout.duck';
import { GeneralPanel } from './components/GeneralPanel';
import { NotificationsPanel } from './components/NotificationsPanel';
import { SignupRegisterPanel } from './components/SignupRegisterPanel';
import { AzureActiveDirectoryPanel } from './components/AzureActiveDirectoryPanel';
import { ApiKeysPanel } from './components/ApiKeysPanel';
import { VisitorCheckinKioskPanel } from './components/VisitorCheckinKioskPanel';
import { IsPublicApiKeyEnabled } from '@shared/hooks/IsPublicApiKeyEnabled';
import { OtherPanel } from './components/OtherPanel';
import { DangerZonePanel } from './components/DangerZonePanel';
import { WhereaboutsVisibilityPanel } from './components/WhereaboutsVisibilityPanel';
import { AutomatedReportingPanel } from './components/AutomatedReportingPanel';
import { WaypointScreensPanel } from './components/WaypointScreensPanel';
import { CompanyAutomaticCheckinPanel } from './components/CompanyAutomaticCheckinPanel';

export function CompanyManagementPageV2() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const activePanel = useSelector(selectActivePanel);
  const editable = user?.role === Permission.CompanyAdmin;
  const isEditable = useMemo(() => canUserUpdateCompanySettings(user), [user]);

  const company = user?.companyEntity;
  // Only shown once "Enable Waypoint screens" is switched on in Other.
  const showWaypointScreens = editable && !!company?.signageEnabled && isPremiumTierUser(user);
  // Premium and Enterprise only (not free trials)
  const showAutomaticCheckin = editable
    && (company?.accountType === AccountType.PREMIUM || company?.accountType === AccountType.ENTERPRISE);
  // SCIM keys live in Azure configuration, so this tab only holds the public API key.
  const showApiKeys = editable && IsPublicApiKeyEnabled();

  const menuItems = useMemo(() => [
    { view: CompanyInformationView.General, icon: IconTypes.Settings, label: 'company-settings-page.general', visible: true },
    { view: CompanyInformationView.Notifications, icon: IconTypes.Notification, label: 'company-settings-page.notifications-tab', visible: true },
    { view: CompanyInformationView.WhereaboutsVisibility, icon: IconTypes.Users, label: 'company-settings-page.whereabouts-management-tab', visible: true },
    { view: CompanyInformationView.SignupRegister, icon: IconTypes.Profile, label: 'company-settings-page.signup-and-register-tab', visible: true },
    { view: CompanyInformationView.AutomatedReporting, icon: IconTypes.Reporting, label: 'company-settings-page.automated-reporting-tab', visible: true },
    { view: CompanyInformationView.AzureActiveDirectory, icon: IconTypes.Reporting, label: 'company-settings-page.azure-configuration', visible: true },
    { view: CompanyInformationView.ApiKeys, icon: IconTypes.Padlock, label: 'company-settings-page.api-keys-tab', visible: showApiKeys },
    { view: CompanyInformationView.VisitorCheckin, icon: IconTypes.Profile, label: 'company-settings-page.visitors-tab', visible: editable },
    { view: CompanyInformationView.WaypointScreens, icon: IconTypes.Compass, label: 'company-settings-page.waypoint-screens-tab', visible: showWaypointScreens },
    { view: CompanyInformationView.AutomaticCheckin, icon: IconTypes.Office, label: 'settings.automatic-checkin', visible: showAutomaticCheckin },
    { view: CompanyInformationView.Other, icon: IconTypes.Settings, label: 'company-settings-page.other-tab', visible: true },
    { view: CompanyInformationView.DangerZone, icon: IconTypes.Info, label: 'company-settings-page.danger-zone', visible: true },
  ].filter(item => item.visible), [editable, showApiKeys, showWaypointScreens, showAutomaticCheckin]);

  useEffect(() => {
    dispatch(reset());
    const qsPanel = getParameterByName('panel', window.location.href);
    if (qsPanel) {
      dispatch(setActivePanel(parseInt(qsPanel)));
    }
    return () => {
      dispatch(reset());
      dispatch(resetEditCompanyLayout());
    };
  }, [dispatch]);

  const initialLoad = useRef(true);
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    setQueryParams([`panel=${activePanel}`]);
  }, [activePanel]);

  const onPanelChange = (panel: CompanyInformationView) => {
    dispatch(setActivePanel(panel));
  };

  if (!company) {
    return null;
  }

  return (
    <SettingsPageLayout>
      <SettingsRibbon title={'company-settings-page.company-settings'} backUrl={'/manage'} />

      {isEditable && (
        <SettingsSidebarLayout menuItems={menuItems}
                               activeView={activePanel}
                               onViewChange={onPanelChange}>
          <AccessRequired visible={!editable} companyAdminsRequired={true} />
          {activePanel === CompanyInformationView.General && <GeneralPanel editable={editable} />}
          {activePanel === CompanyInformationView.Notifications && <NotificationsPanel />}
          {activePanel === CompanyInformationView.WhereaboutsVisibility && <WhereaboutsVisibilityPanel />}
          {activePanel === CompanyInformationView.SignupRegister && <SignupRegisterPanel />}
          {activePanel === CompanyInformationView.AzureActiveDirectory && <AzureActiveDirectoryPanel editable={editable} />}
          {activePanel === CompanyInformationView.ApiKeys && showApiKeys && <ApiKeysPanel />}
          {activePanel === CompanyInformationView.VisitorCheckin && editable && <VisitorCheckinKioskPanel />}
          {activePanel === CompanyInformationView.WaypointScreens && showWaypointScreens && <WaypointScreensPanel />}
          {activePanel === CompanyInformationView.AutomaticCheckin && showAutomaticCheckin && <CompanyAutomaticCheckinPanel />}
          {activePanel === CompanyInformationView.AutomatedReporting && <AutomatedReportingPanel />}
          {activePanel === CompanyInformationView.Other && <OtherPanel onOpenWaypointScreens={() => onPanelChange(CompanyInformationView.WaypointScreens)} />}
          {activePanel === CompanyInformationView.DangerZone && <DangerZonePanel editable={editable} />}
        </SettingsSidebarLayout>
      )}
    </SettingsPageLayout>
  );
}
